import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';
import { Box, Tab, Tabs } from '@mui/material';
import TabPanel from 'components/TabPanel/TabPanel';
import QueryPurchaseHistory from './ReportProductQuery/QueryPurchaseHistory';
import QuerySaleHistory from './ReportProductQuery/QuerySaleHistory';
import QueryStock from './ReportProductQuery/QueryStock';
import QueryProductPrice from './ReportProductQuery/QueryProductPrice';
import QueryProductCampaign from './ReportProductQuery/QueryProductCampaign';
import SearchProduct from './ReportProductQuery/SearchProduct';

const initProduct = {
  code: '',
  name: '',
};

function RpProductQuery() {
  const mod = 'rp_product_query';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [product, setProduct] = useState(initProduct);
  const [tab, setTab] = useState(0);
  const [urlExcel, setUrlExcel] = useState('');

  const select = (item) => {
    FetchData('OPTIONS', `/${mod}/get_product`, app.token, {
      barcode_id: item.barcode_id,
    }).then((result) => {
      if (result.status === 'ok') {
        if (!result.data) {
          setProduct(initProduct);
        } else {
          setProduct({ code: result.data.code, name: result.data.name });
        }
      } else {
        Func.exitApp();
      }
    });
  };

  const excel = () => {
    const params = new URLSearchParams();
    params.append('search', product.code);
    params.append('id', tab);
    window.open(`${urlExcel}/download?${params.toString()}`, '_self');
  };

  const renderHeader = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_power_bi'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            <Card sx={{ p: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6} lg={2}>
                  <SearchProduct add={select} />
                </Grid>
                {product.code !== '' && (
                  <Grid item xs={12} sm={12} md={12} lg={1}>
                    <MDBox display='flex' justifyContent='end' gap={1}>
                      <MDButton variant='gradient' color='success' onClick={excel}>
                        <Icon>table_view</Icon>&nbsp;Excel
                      </MDButton>
                    </MDBox>
                  </Grid>
                )}
              </Grid>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6} md={6} lg={12}>
                  <MDBox>
                    <MDTypography variant='h5'>
                      รหัสสินค้า :&nbsp;
                      <span style={{ color: 'blue' }}>
                        {product.code === '' ? 'ไม่พบสินค้า' : product.code}
                      </span>
                      &nbsp;&nbsp;&nbsp;&nbsp;&nbsp; ชื่อสินค้า :&nbsp;
                      <span style={{ color: 'blue' }}>
                        {product.name === '' ? 'ไม่พบสินค้า' : product.name}
                      </span>
                    </MDTypography>
                  </MDBox>
                </Grid>
              </Grid>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Tabs value={tab} onChange={(e, v) => setTab(v)} aria-label='tabs-addedit'>
                <Tab label='ประวัติการซื้อ' />
                <Tab label='ประวัติการขาย' />
                <Tab label='จำนวนคงเหลือ' />
                <Tab label='ราคาขายสินค้า' />
                <Tab label='แคมเปญขาย' />
              </Tabs>
              <TabPanel value={tab} index={0}>
                {tab === 0 && (
                  <QueryPurchaseHistory productCode={product.code} setLoading={setLoading} />
                )}
              </TabPanel>
              <TabPanel value={tab} index={1}>
                {tab === 1 && (
                  <QuerySaleHistory productCode={product.code} setLoading={setLoading} />
                )}
              </TabPanel>
              <TabPanel value={tab} index={2}>
                {tab === 2 && <QueryStock productCode={product.code} setLoading={setLoading} />}
              </TabPanel>
              <TabPanel value={tab} index={3}>
                {tab === 3 && (
                  <QueryProductPrice productCode={product.code} setLoading={setLoading} />
                )}
              </TabPanel>
              <TabPanel value={tab} index={4}>
                {tab === 4 && (
                  <QueryProductCampaign productCode={product.code} setLoading={setLoading} />
                )}
              </TabPanel>
            </TableContainer>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setLoading(false);
        setUrlExcel(result.excel);
      } else {
        Func.exitApp();
      }
    });
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);
    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_power_bi`), t(`menu.rp_product_query`)]} />
      {renderHeader()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpProductQuery;
